import React, { useState } from 'react';
import './ImageManager.css';
import { updateImage, deleteImage } from '../../firebase/dataService';
import DeleteConfirmModal from './DeleteConfirmModal';

function ImageManager({ images, onImageUpdated, onImageClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVisible, setFilterVisible] = useState('all');
  const [editingImage, setEditingImage] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const imagesPerPage = 20; // نفس عدد الصور في الموقع الرئيسي

  const filteredImages = images.filter(img => {
    const matchesSearch = !searchTerm || 
      img.caption?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      img.extractedText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      img.category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterVisible === 'all' ||
      (filterVisible === 'visible' && img.visible !== false) ||
      (filterVisible === 'hidden' && img.visible === false);
    
    return matchesSearch && matchesFilter;
  });

  // حساب الصفحات
  const totalPages = Math.ceil(filteredImages.length / imagesPerPage);
  const startIndex = (currentPage - 1) * imagesPerPage;
  const endIndex = startIndex + imagesPerPage;
  const currentImages = filteredImages.slice(startIndex, endIndex);

  const handleToggleVisibility = async (image) => {
    try {
      setLoading(true);
      const newVisibility = image.visible === false ? true : false;
      await updateImage(image.id, { visible: newVisibility });
      onImageUpdated();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      alert('حدث خطأ في تحديث حالة الظهور');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (image) => {
    setImageToDelete(image);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;
    
    setDeleting(true);

    try {
      await deleteImage(imageToDelete.id);
      if (onImageUpdated) {
        onImageUpdated();
      }
      setShowDeleteModal(false);
      setImageToDelete(null);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('حدث خطأ أثناء حذف التصميم');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setImageToDelete(null);
  };

  const handleEdit = (image) => {
    setEditingImage(image.id);
    setEditForm({
      caption: image.caption || '',
      category: image.category || '',
      extractedText: image.extractedText || ''
    });
  };

  const handleSaveEdit = async (imageId) => {
    try {
      setLoading(true);
      await updateImage(imageId, editForm);
      setEditingImage(null);
      onImageUpdated();
      alert('تم تحديث التصميم بنجاح');
    } catch (error) {
      console.error('Error updating image:', error);
      alert('حدث خطأ في تحديث التصميم');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingImage(null);
    setEditForm({});
  };

  return (
    <div className="image-manager">
      <div className="manager-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 البحث في التصاميم..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-buttons">
          <button
            className={filterVisible === 'all' ? 'active' : ''}
            onClick={() => setFilterVisible('all')}
          >
            الكل ({images.length})
          </button>
          <button
            className={filterVisible === 'visible' ? 'active' : ''}
            onClick={() => setFilterVisible('visible')}
          >
            المرئية ({images.filter(img => img.visible !== false).length})
          </button>
          <button
            className={filterVisible === 'hidden' ? 'active' : ''}
            onClick={() => setFilterVisible('hidden')}
          >
            المخفية ({images.filter(img => img.visible === false).length})
          </button>
        </div>
      </div>

      <div className="images-grid">
        {filteredImages.length === 0 ? (
          <div className="no-results">
            <p>📭 لا توجد نتائج</p>
          </div>
        ) : (
          currentImages.map(image => (
            <div 
              key={image.id} 
              className={`image-card ${image.visible === false ? 'hidden-image' : ''}`}
            >
              <div 
                className="image-preview"
                onClick={() => onImageClick && onImageClick(image)}
                style={{ cursor: 'pointer' }}
              >
                <img src={image.url} alt={image.caption} />
                {image.visible === false && (
                  <div className="hidden-badge">🙈 مخفي</div>
                )}
                <div className="view-overlay">
                  <span className="view-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </span>
                  <span className="view-text">عرض التفاصيل</span>
                </div>
              </div>

              {editingImage === image.id ? (
                <div className="edit-form">
                  <input
                    type="text"
                    value={editForm.caption}
                    onChange={(e) => setEditForm({...editForm, caption: e.target.value})}
                    placeholder="العنوان"
                  />
                  <input
                    type="text"
                    value={editForm.category}
                    onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                    placeholder="التصنيف"
                  />
                  <textarea
                    value={editForm.extractedText}
                    onChange={(e) => setEditForm({...editForm, extractedText: e.target.value})}
                    placeholder="النص المستخرج"
                    rows="3"
                  />
                  <div className="edit-actions">
                    <button 
                      className="save-btn"
                      onClick={() => handleSaveEdit(image.id)}
                      disabled={loading}
                    >
                      ✅ حفظ
                    </button>
                    <button 
                      className="cancel-btn"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      ❌ إلغاء
                    </button>
                  </div>
                </div>
              ) : (
                <div className="image-info">
                  <h3>{image.caption || 'بدون عنوان'}</h3>
                  {image.category && (
                    <span className="category-badge">{image.category}</span>
                  )}
                  {image.extractedText && (
                    <p className="extracted-text">
                      {image.extractedText.substring(0, 100)}
                      {image.extractedText.length > 100 ? '...' : ''}
                    </p>
                  )}
                </div>
              )}

              {editingImage !== image.id && (
                <div className="image-card-actions">
                  <button
                    className="img-action-btn img-visibility-btn"
                    onClick={() => handleToggleVisibility(image)}
                    disabled={loading}
                    title={image.visible === false ? 'إظهار' : 'إخفاء'}
                  >
                    {image.visible === false ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '4px' }}>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        إظهار
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '4px' }}>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                        إخفاء
                      </>
                    )}
                  </button>
                  <button
                    className="img-action-btn img-delete-btn"
                    onClick={() => handleDeleteClick(image)}
                    disabled={loading}
                    title="حذف"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginLeft: '4px' }}>
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    حذف
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* أزرار التنقل بين الصفحات */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            → السابق
          </button>
          
          <div className="pagination-info">
            صفحة {currentPage} من {totalPages}
          </div>
          
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            ← التالي
          </button>
        </div>
      )}

      {showDeleteModal && imageToDelete && (
        <DeleteConfirmModal
          title="حذف التصميم"
          message={`هل أنت متأكد من حذف التصميم "${imageToDelete.caption}"؟`}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={deleting}
        />
      )}
    </div>
  );
}

export default ImageManager;
